import { QueryClient, useMutation, useQuery, useQueryClient, UseQueryOptions, UseQueryResult } from 'react-query';
import { Customer } from 'src/types/Customer';
import { CustomerMergeLog } from 'src/types/CustomerMergeLog';
import { UserCustomer } from 'src/types/UserCustomer';
import { useAggregatedQueries } from 'src/hooks/useAggregatedQuery';
import { useFileUploader } from 'src/hooks/useFileUploader';
import { get, post, put } from '../utils';
import { getPage, Page, PageRequest, sortValueFrom } from '../pagination';
import { CustomerDeviceUsage } from '../../../types/CustomerDeviceUsage';

export function createCustomer(name: string, tetherDescription?: string, isVerified?: boolean): Promise<Customer> {
  const body = {
    name,
    tetherDescription,
    isVerified,
  };
  return post<Customer>('customer', body);
}

export function updateCustomer(customer: Customer) {
  return put<Customer>(`customer/${customer.id}`, { ...customer });
}

/** Mints a presigned PUT for a new customer logo and returns it with the canonical public URL to save
 *  as the customer's logoUrl. The API pins Content-Type and the exact size on the PUT, only accepts
 *  PNG/JPEG (the certificate renderer can't embed anything else) up to 2 MB, and binds the object to
 *  this customer. */
export function getCustomerLogoUploadUrl(
  customerId: string,
  contentType: string,
  fileSize: number,
): Promise<{ uploadUrl: string; logoUrl: string }> {
  return post<{ uploadUrl: string; logoUrl: string }>(`customer/${customerId}/logo-upload-url`, { contentType, fileSize });
}

/** Two-step logo upload (the OAuth client logo pattern): ask the API for a presigned PUT, raw-PUT the
 *  bytes to S3, then resolve the public logoUrl. Nothing is saved on the customer here — the caller puts
 *  the URL into the edit form and it is persisted by the normal customer Save. */
export function useUploadCustomerLogoMutation(customerId: string) {
  const { uploadFile } = useFileUploader();
  return useMutation<string, Error, File>(async (file: File) => {
    const { uploadUrl, logoUrl } = await getCustomerLogoUploadUrl(customerId, file.type, file.size);
    const uploadSucceeded = await uploadFile(uploadUrl, file);
    if (!uploadSucceeded) {
      throw new Error(`Failed to upload ${file.name}`);
    }
    return logoUrl;
  });
}

export function getCustomerById(customerId: string): Promise<Customer> {
  return get<Customer>(`customer/${customerId}`, { includeDeleted: true });
}

export function getCustomerAdmins(customerId: string): Promise<UserCustomer[]> {
  return get<UserCustomer>(`usercustomer`, { customerId });
}

export function getCustomerDeviceUsages(customerId: string): Promise<CustomerDeviceUsage[]> {
  return get<CustomerDeviceUsage>(`customer/${customerId}/device/usage`);
}

type NewCustomerAdmin = {
  userId: string;
  customerId: string;
  roleId: 'admin';
};

export function createCustomerAdmin(customerAdmin: NewCustomerAdmin): Promise<UserCustomer> {
  return post<UserCustomer>('usercustomer', customerAdmin);
}

export function useQueryCustomerById(customerId: string, options: UseQueryOptions<Customer, Error> = {}): UseQueryResult<Customer, Error> {
  return useQuery(['Customer', customerId], () => getCustomerById(customerId), options);
}

export function useQueryCustomersById(customerIds: string[], options?: UseQueryOptions<Customer>) {
  return useAggregatedQueries<Customer>(
    customerIds.map((customerId) => ({
      queryKey: ['Customer', customerId],
      queryFn: () => getCustomerById(customerId),
      ...options,
    }))
  );
}

export function searchCustomers(name: string, includeDeleted: boolean = false): Promise<Customer[]> {
  return get<Customer[]>('customer', { name, includeDeleted });
}

export function useQueryCustomers(
  name: string,
  includeDeleted: boolean = false,
  options?: UseQueryOptions<Customer[]>
): UseQueryResult<Customer[]> {
  return useQuery(['CustomerSearch', name, includeDeleted], () => searchCustomers(name, includeDeleted), options);
}

export function mergeCustomers(sourceCustomerId: string, destinationCustomerId: string): Promise<void> {
  return post('customermerge', {}, { sourceCustomerId, destinationCustomerId });
}

export function getCustomerMergeLog(customerId: string): Promise<CustomerMergeLog[]> {
  return get<CustomerMergeLog[]>(`customer/${customerId}/mergelog`);
}

export function useQueryCustomerMergeLog(customerId: string): UseQueryResult<CustomerMergeLog[]> {
  return useQuery(['CustomerMergeLog', customerId], () => getCustomerMergeLog(customerId));
}


/** Sort fields the customer search accepts. Mirrors the API's CUSTOMER_SORT_FIELDS. */
export const CUSTOMER_SORT_FIELDS = ['name', 'createdAt', 'isVerified'] as const;

/** How a row is ordered by each of {@link CUSTOMER_SORT_FIELDS}. */
export const getCustomerSortValue = sortValueFrom<Customer>({
  name: (customer) => customer.name,
  createdAt: (customer) => customer.createdAt,
  // Sorted as a number so it orders the way the server's tinyint column does: not
  // verified (0) first ascending. As a boolean, JS would compare `false < true` the same
  // way, but the fallback comparator only handles strings and numbers.
  isVerified: (customer) => (customer.isVerified ? 1 : 0),
});

/**
 * ONE page of the customer search, with the total.
 *
 * The Customer List asked for `?name=%` — every customer on the platform — and rendered
 * all of them, then counted, searched and sorted them in the browser. Its footer total was
 * the size of the download rather than a fact the screen could have known any other way.
 */
export function getCustomersPage(
  request: PageRequest,
  includeDeleted: boolean,
  queryClient?: QueryClient
): Promise<Page<Customer>> {
  return getPage<Customer>(
    'customer',
    request,
    // `name: '%'` is the FLAG-OFF fallback, and it is load-bearing rather than legacy
    // residue. While ORG_SCALE_PAGINATION is off the server ignores the paging parameters
    // and takes its legacy branch — which REQUIRES a name and answers with an EMPTY ARRAY
    // without one. A wildcard is what that branch has always been given by this screen, so
    // the flag-off response is the same full list it serves today, which getPage then
    // searches, sorts and windows. The paged branch applies it too, where it matches
    // everything and narrows nothing.
    { name: '%', includeDeleted },
    {
      // The legacy branch matches on name alone. Without these the search box would answer
      // with the first N customers on the platform while the flag is off — not an error
      // and not an empty state, a confident wrong answer.
      searchable: (customer) => [customer.id, customer.name, customer.tetherDescription ?? ''],
      sortValue: getCustomerSortValue,
    },
    queryClient,
    // The same base key `useCustomersPage` queries under, so CustomerListTable's post-merge
    // `invalidateQueries('CustomerPage')` drops the shared whole list as well as the page.
    ['CustomerPage']
  );
}

/** One page of customers, as a query. */
export function useCustomersPage(
  request: PageRequest,
  includeDeleted: boolean
): UseQueryResult<Page<Customer>, Error> {
  // Shares the FLAG-OFF whole-list download across pages, sorts and keystrokes.
  const queryClient = useQueryClient();
  return useQuery<Page<Customer>, Error>(
    ['CustomerPage', request, includeDeleted],
    () => getCustomersPage(request, includeDeleted, queryClient),
    {
      // The rows stay while the next page loads, so paging dims the table instead of
      // emptying it. `isLoading` is false throughout under this — callers must read
      // `isFetching` to know a request is in flight.
      keepPreviousData: true,
    }
  );
}
